import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface Anchor {
  image: string
  name: string
  rect: DOMRect
}

/**
 * Hover (or tap) a pulled prize to see the slab. The preview is rendered in a
 * portal with fixed positioning so the scrolling feed never clips it.
 */
export function PrizeName({ name, image }: { name: string; image: string | null }) {
  const [anchor, setAnchor] = useState<Anchor | null>(null)
  if (!image) return <em>{name}</em>
  const show = (el: HTMLElement) => setAnchor({ image, name, rect: el.getBoundingClientRect() })
  return (
    <>
      <em
        className="prize"
        tabIndex={0}
        onMouseEnter={(e) => show(e.currentTarget)}
        onMouseLeave={() => setAnchor(null)}
        onFocus={(e) => show(e.currentTarget)}
        onBlur={() => setAnchor(null)}
        onClick={(e) => (anchor ? setAnchor(null) : show(e.currentTarget))}
      >
        {name}
      </em>
      {anchor ? <Preview anchor={anchor} /> : null}
    </>
  )
}

function Preview({ anchor }: { anchor: Anchor }): ReactNode {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)
  // A cached image can be complete before React attaches onLoad; check once after mount.
  useEffect(() => {
    setLoaded(imgRef.current?.complete === true && imgRef.current.naturalWidth > 0)
  }, [anchor.image])
  const W = 240
  const H = 360
  const vw = window.innerWidth
  const vh = window.innerHeight
  // Prefer the right of the name, then the left, then above; clamp to the viewport.
  let left = anchor.rect.right + 12
  if (left + W > vw - 8) left = anchor.rect.left - W - 12
  if (left < 8) left = Math.min(Math.max(8, anchor.rect.left), vw - W - 8)
  let top = anchor.rect.top + anchor.rect.height / 2 - H / 2
  top = Math.min(Math.max(8, top), vh - H - 8)
  return createPortal(
    <div className={`prize-preview${loaded ? ' loaded' : ''}`} style={{ left, top, width: W, height: H }} role="img" aria-label={anchor.name}>
      <img ref={imgRef} src={anchor.image} alt="" onLoad={() => setLoaded(true)} />
      <span className="prize-preview-name">{anchor.name}</span>
    </div>,
    document.body,
  )
}
